// Thin client for the internal Email cockpit API (/api/v2/email_management/*). Reuses the app's SHARED
// client (Api.default) so it carries the /api/v2 base path AND the x-company-id header that
// companyLoader sets -- that header is what establishes the current person/company on the server.
// A fresh ApiClient() would lack the header and 500 with "current_person not found".
import Api from "@/api";

const client = Api.default;

export interface EmailIdentity {
  id: string;
  address: string;
  displayName: string;
  roleTitle?: string | null;
  lane: string;
  status: string;
  dailyCap: number;
  targetCap: number;
  step: number;
  minCap: number;
  sentToday: number;
  dayIndex: number;
  domain?: string | null;
  domainId?: string | null;
  mailboxConfigured: boolean;
  /** Inbox-placement monitoring via TrulyInbox. */
  monitored?: boolean;
  placementRate?: number | null;
  placementFloor?: number | null;
  placementCheckedAt?: string | null;
  deliverabilityHeldAt?: string | null;
  deliverabilityHoldReason?: string | null;
  spaces: { id: string; name: string }[];
}

export interface EmailOverview {
  summary: { sentToday: number; capToday: number; backlog: number };
  identities: EmailIdentity[];
  domains: { id: string; domain: string; kind: string; provider: string }[];
  spaces: { id: string; name: string }[];
}

export async function getOverview(): Promise<EmailOverview> {
  const res: any = await client.get("/email_management/get_overview", {});
  return res.overview as EmailOverview;
}

export async function createDomain(input: { domain: string; kind?: string; provider?: string }): Promise<any> {
  return client.post("/email_management/create_domain", input);
}

export async function createIdentity(input: {
  domainId: string;
  address: string;
  displayName: string;
  roleTitle?: string;
  signature?: string;
  lane?: string;
  smtpPasswordRef?: string;
  dailyCap?: number;
  targetCap?: number;
  step?: number;
  minCap?: number;
}): Promise<any> {
  return client.post("/email_management/create_identity", input);
}

export async function assignSpace(input: { identityId: string; spaceId: string }): Promise<any> {
  return client.post("/email_management/assign_space", input);
}

export async function unassignSpace(input: { identityId: string; spaceId: string }): Promise<any> {
  return client.post("/email_management/unassign_space", input);
}

/* ─────────────────────────── the inbox ─────────────────────────── */

export interface InboxConversation {
  conversationId: string;
  counterpartEmail: string;
  counterpartName?: string | null;
  subject?: string | null;
  status: string;
  owningSpaceId?: string | null;
  identityAddress?: string | null;
  identityName?: string | null;
  lastAt?: string | null;
  lastDirection?: string | null;
  lastAuthorKind?: string | null;
  preview: string;
  messageCount: number;
  awaitingReply: boolean;
  takenOver: boolean;
  takenOverBy?: string | null;
  takenOverAt?: string | null;
  /** Activity newer than any operator's last open of the thread (shared team read-state). */
  unread: boolean;
}

export interface InboxPage {
  conversations: InboxConversation[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConversationMessage {
  id: string;
  direction: "outbound" | "inbound";
  authorKind: "agent" | "human" | "counterpart";
  authorName?: string | null;
  from: string;
  to: string;
  subject?: string | null;
  body?: string | null;
  status: string;
  error?: string | null;
  at?: string | null;
  sentAt?: string | null;
}

export interface ConversationDetail {
  conversationId: string;
  companyId: string;
  counterpartEmail: string;
  counterpartName?: string | null;
  subject?: string | null;
  status: string;
  owningSpaceId?: string | null;
  identityAddress?: string | null;
  identityName?: string | null;
  identityRole?: string | null;
  lastAt?: string | null;
  messages: ConversationMessage[];
  takenOver: boolean;
  takenOverBy?: string | null;
  takenOverAt?: string | null;
  takeoverReason?: string | null;
  handoffAlert?: HandoffAlert | null;
}

/** Live hot-lead alert on a conversation: "nagging" = SMS ladder running, "acked" = seen but unanswered. */
export interface HandoffAlert {
  id: string;
  status: "nagging" | "acked";
  headline?: string | null;
  nagCount: number;
  createdAt?: string | null;
}

export async function getInbox(input: {
  search?: string;
  ownership?: "all" | "human" | "agent";
  awaitingOnly?: boolean;
  limit?: number;
  offset?: number;
}): Promise<InboxPage> {
  const res: any = await client.get("/email_management/get_inbox", input);
  return res.inbox as InboxPage;
}

export async function getConversation(conversationId: string): Promise<ConversationDetail | null> {
  const res: any = await client.get("/email_management/get_conversation", { conversationId });
  return (res.conversation ?? null) as ConversationDetail | null;
}

export async function takeOverConversation(input: { conversationId: string; reason?: string }): Promise<any> {
  return client.post("/email_management/take_over_conversation", input);
}

export async function releaseConversation(conversationId: string): Promise<any> {
  return client.post("/email_management/release_conversation", { conversationId });
}

export async function replyToConversation(input: {
  conversationId: string;
  body: string;
  subject?: string;
}): Promise<any> {
  return client.post("/email_management/reply_to_conversation", input);
}

export async function acknowledgeHandoff(conversationId: string): Promise<any> {
  return client.post("/email_management/acknowledge_handoff", { conversationId });
}

export async function updateIdentity(input: {
  id: string;
  status?: string;
  signature?: string;
  displayName?: string;
  roleTitle?: string;
  smtpPasswordRef?: string;
  dailyCap?: number;
  targetCap?: number;
  step?: number;
  minCap?: number;
}): Promise<any> {
  return client.post("/email_management/update_identity", input);
}
